<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { DgrCheckbox, DgrIcon, DgrPopover } from '@stockmarkteam/donguri-ui';
import SearchBar from '@/components/common/molecules/search-bar.vue';
import { TagSelectItem } from '@/types';

const props = withDefaults(
  defineProps<{
    tagList: TagSelectItem[];
    targetTags: string[];
    placeholder: string;
    disabled?: boolean;
    emptyFilterMessage?: string;
    isShowCount?: boolean;
    size?: 'small' | 'medium';
  }>(),
  {
    disabled: false,
    emptyFilterMessage: '指定したタグはありません。',
    isShowCount: true,
    size: 'small',
  },
);

const emit = defineEmits<{
  'filter-articles': [tags: string[]];
}>();

const filterQuery = ref('');
const filteredArticleTags = computed(() => {
  return props.tagList.filter(tag => {
    return tag.name.includes(filterQuery.value);
  });
});

const selectedTags = ref([...props.targetTags]);
const selectTag = (tagName: string, checked: boolean): void => {
  if (checked) {
    selectedTags.value.push(tagName);
  } else {
    const index = selectedTags.value.indexOf(tagName);
    selectedTags.value.splice(index, 1);
  }
};
const isSelectedTag = (tagName: string) => selectedTags.value.includes(tagName);
watch(
  () => props.targetTags,
  targetTags => {
    selectedTags.value = [...targetTags];
  },
);

const handleSelectAllTags = (): void => {
  const filteredTagNameList = filteredArticleTags.value.map(tag => {
    return tag.name;
  });
  filteredTagNameList.forEach(tag => {
    const index = selectedTags.value.indexOf(tag);
    if (index >= 0) {
      selectedTags.value.splice(index, 1);
    }
  });
  selectedTags.value.push(...filteredTagNameList);
};

const handleDeselectAllTags = (): void => {
  const filteredTagNameList = filteredArticleTags.value.map(tag => {
    return tag.name;
  });
  filteredTagNameList.forEach(tagName => {
    const index = selectedTags.value.indexOf(tagName);
    if (index >= 0) {
      selectedTags.value.splice(index, 1);
    }
  });
};

const handleTagFilterArticles = (): void => {
  emit('filter-articles', selectedTags.value);
};

// 画面縦幅が狭いと上方向にドロップダウンが表示されるが、そのときに
// ヘッダーの下に潜り込まないように--z-modalと同じz-indexを指定しておく
const dropdownZIndex = Number(
  getComputedStyle(document.body).getPropertyValue('--z-modal'),
);
</script>

<template>
  <DgrPopover :z-index="dropdownZIndex">
    <template #default="{ triggerProps }">
      <button
        type="button"
        v-bind="triggerProps"
        class="c-selectBox"
        :class="
          size === 'small'
            ? ['c-selectBox--small', 'c-text-regular--s']
            : ['c-text-regular--m']
        "
        style="line-height: 12px"
        :disabled="props.disabled"
      >
        {{ placeholder }}

        <DgrIcon
          name="angle-down"
          :size="size === 'small' ? 'small' : 'default'"
          class="c-selectBox__arrow"
          :keep-fill="false"
        />
      </button>
    </template>
    <template #content="{ close }">
      <div class="tag-select">
        <div class="tag-search-bar">
          <SearchBar v-model="filterQuery"></SearchBar>
        </div>
        <div class="tag-list">
          <div
            class="tags-empty c-text-regular--m"
            v-if="
              (!tagList || tagList.length === 0) && filterQuery.length === 0
            "
          >
            未設定
          </div>
          <div
            class="tags-empty c-text-regular--m"
            v-if="filteredArticleTags.length === 0 && filterQuery.length > 0"
          >
            {{ emptyFilterMessage }}
          </div>
          <div class="tag" v-for="tag in filteredArticleTags" :key="tag.id">
            <DgrCheckbox
              class="checkbox-label c-text-regular--m"
              :model-value="isSelectedTag(tag.name)"
              @update:model-value="selectTag(tag.name, $event)"
              >{{
                isShowCount ? tag.name + '(' + tag.count + ')' : tag.name
              }}</DgrCheckbox
            >
          </div>
        </div>
        <div class="btn-area">
          <button
            class="select-all-btn c-text-regular--m"
            @click="handleSelectAllTags"
          >
            すべて選択
          </button>
          <button
            class="select-deselect-btn c-text-regular--m"
            @click="handleDeselectAllTags"
          >
            選択解除
          </button>
          <button
            class="tag-filter-btn c-btn c-btn--small c-btn--AnewsPrimary"
            @click="
              handleTagFilterArticles();
              close();
            "
            data-testid="tag-filter-apply-btn"
          >
            適用
          </button>
        </div>
      </div>
    </template>
  </DgrPopover>
</template>

<style lang="scss" scoped>
button[disabled] {
  color: $color-gray600;
  cursor: not-allowed;

  .c-selectBox__arrow {
    color: $color-gray600;
  }

  &:hover {
    background: white;
  }
}

.tag-select {
  z-index: var(--z-dropdown);
  width: 320px;
  padding-top: 16px;
  background: #ffffff;
  border: 1px solid $color-gray400;
  box-sizing: border-box;
  box-shadow: 0px 1px 5px rgba($color-gray1000, 0.25);
  border-radius: 4px;

  .tag-search-bar {
    margin-bottom: 20px;
    padding: 0 16px;
  }

  .tag-list {
    max-height: 240px;
    overflow-y: scroll;
    padding: 0 16px;

    .tags-empty {
      color: $color-gray600;
      margin-bottom: 14px;
    }

    .tag {
      .checkbox-label {
        display: block;
        margin-bottom: 8px;
        word-break: break-word;
      }
    }
  }

  .btn-area {
    display: flex;
    align-items: center;
    padding: 12px;
    border-top: 1px solid $color-gray400;

    .select-all-btn,
    .select-deselect-btn {
      height: 32px;
      padding: 4px;
      background-color: transparent;
      color: $color-green600;
      border: none;
      cursor: pointer;
      align-items: center;
      border-radius: 4px;

      &:hover {
        background-color: $color-gray200;
      }
    }

    .select-all-btn {
      margin-right: 8px;
    }

    .tag-filter-btn {
      margin: 0 0 0 auto;
      width: 60px;
      align-items: center;
    }
  }
}
</style>
